/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import {
  OperationalFilterSearchField,
  OperationalFilterSegmentedRow,
  type OperationalFilterOption,
} from './OperationalFilterPrimitives';
import { spacing, ticketColors, ticketFonts } from '../theme/public-api';

export type MastersPageSize = 5 | 10;

type Props<T extends string> = {
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  searchPlaceholder: string;
  searchLabel?: string;
  stateOptions: Array<OperationalFilterOption<T>>;
  stateCounts?: Partial<Record<T, number>>;
  filterState: T;
  onFilterStateChange: (value: T) => void;
  pageSize: MastersPageSize;
  onPageSizeChange: (size: MastersPageSize) => void;
  layoutWide?: boolean;
  /** Proto toolbar: CSV / Agregar / acciones contextuales al final. */
  trailingActions?: React.ReactNode;
};

export function MastersListFiltersPanel<T extends string>({
  searchQuery,
  onSearchQueryChange,
  searchPlaceholder,
  searchLabel,
  stateOptions,
  stateCounts,
  filterState,
  onFilterStateChange,
  layoutWide = false,
  trailingActions,
}: Props<T>) {
  const countedOptions = stateOptions.map((option) => {
    const count = stateCounts?.[option.value];
    return count == null ? option : { ...option, label: `${option.label} ${count}` };
  });

  return (
    <View style={[styles.toolbar, layoutWide ? styles.toolbarWide : styles.toolbarCompact]}>
      <View style={styles.searchBlock}>
        <Text style={styles.controlLabel}>{searchLabel ?? 'Buscar'}</Text>
        <OperationalFilterSearchField
          value={searchQuery}
          onChangeText={onSearchQueryChange}
          placeholder={searchPlaceholder}
          appearance="desk"
          dense
          autoCapitalize="none"
          accessibilityLabel={searchPlaceholder}
        />
      </View>

      <View style={[styles.stateBlock, !layoutWide && styles.stateBlockCompact]}>
        <Text style={styles.filterLabel}>Estado</Text>
        <OperationalFilterSegmentedRow
          ticketTone
          dense
          selectedStyle="solid"
          layout={layoutWide ? 'hug' : 'stretch'}
          options={countedOptions}
          value={filterState}
          onChange={onFilterStateChange}
        />
      </View>

      {trailingActions ? (
        <View style={[styles.actionsBlock, !layoutWide && styles.actionsBlockCompact]}>
          <View style={styles.actionsRow}>{trailingActions}</View>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  toolbar: {
    marginBottom: spacing.sm,
    paddingBottom: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
  },
  toolbarWide: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 10,
  },
  toolbarCompact: {
    gap: 10,
  },
  searchBlock: {
    flex: 1,
    minWidth: 170,
    gap: 5,
  },
  controlLabel: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.4,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  filterLabel: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  stateBlock: {
    gap: 5,
    flexShrink: 0,
  },
  stateBlockCompact: {
    alignSelf: 'stretch',
  },
  actionsBlock: {
    gap: 5,
    flexShrink: 0,
    marginLeft: 'auto',
  },
  actionsBlockCompact: {
    alignSelf: 'stretch',
    marginLeft: 0,
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 7,
    flexWrap: 'wrap',
  },
});
